/**
 * WEB-854 — ветка «приложение уже установлено».
 *
 * Раньше кнопка возврата вела по схеме приложения БЕЗУСЛОВНО: если
 * приложения на устройстве нет, клик по `myapp://…` молча ничего не делает —
 * покупатель упирается в мёртвый экран.
 *
 * Детекции установки не существует ни у одной платформы (и у нас тоже),
 * поэтому механика — «попытка + запасной путь»:
 *   1) пробуем открыть приложение по его схеме;
 *   2) заводим короткий таймер;
 *   3) если страница ВСЁ ЕЩЁ видна к моменту срабатывания — приложение не
 *      открылось, уводим в магазин.
 * Открывшееся приложение уводит страницу в фон → visibilitychange, таймер
 * снимается и магазин НЕ открывается.
 *
 * Отпечаток устройства и буфер обмена НЕ используем (осознанно отложены).
 *
 * Про «мёртвый экран»: любой исход должен что-то сделать. Схемы нет или
 * десктоп — сразу магазин; нет вообще ничего — возвращаем 'none', и вызывающий
 * говорит человеку словами, а не молчит.
 */
import { deeplinkNavigate } from '@/utils/deeplinkNavigate';

/**
 * Сколько ждём приложение до ухода в магазин. Взято равным
 * DEEPLINK_FALLBACK_MS страницы HandoffSuccess (WEB-356 #4) — там та же
 * механика уже обкатана, разъезжаться двум задержкам незачем.
 */
export const APP_ATTEMPT_TIMEOUT_MS = 2000;

export type StorePlatform = 'ios' | 'android' | 'other';

/**
 * Платформа посетителя для ВЫБОРА МАГАЗИНА.
 *
 * ⚠ Шрам WEB-828: Android проверяется ПЕРВЫМ — его User-Agent содержит и
 * «Linux», и «Mobile», и «Safari», поэтому любая широкая проверка «мобильный
 * Safari» захватила бы Android и увела бы покупателя в чужой стор. Зеркало
 * backend/src/handoff/utils/detect-platform.ts, где порядок такой же и по той
 * же причине.
 *
 * Неизвестное — 'other', а не догадка: лучше поведение по умолчанию, чем
 * неверный магазин.
 */
export function detectStorePlatform(
  userAgent: string | undefined | null,
): StorePlatform {
  if (!userAgent) return 'other';
  const ua = userAgent.toLowerCase();
  if (ua.includes('android')) return 'android';
  if (
    ua.includes('iphone') ||
    ua.includes('ipad') ||
    ua.includes('ipod')
  ) {
    return 'ios';
  }
  return 'other';
}

export interface StoreTargets {
  /**
   * Ссылка на магазин с меткой перехода (opaque install-token в referrer /
   * MMP-параметре, WEB-433). Сильнее голой ссылки на магазин: именно она
   * доносит атрибуцию до установки — иначе метка теряется.
   */
  installUrlIos?: string | null;
  installUrlAndroid?: string | null;
  /** Голые ссылки на магазины из настроек проекта. */
  iosStoreUrl?: string | null;
  androidStoreUrl?: string | null;
}

function blank(value: string | null | undefined): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * Куда вести покупателя, если приложения нет. ОДНО правило на весь продукт
 * (WEB-1910, решение владельца Q116 06.09 — «вести на имеющийся магазин»):
 *
 *   · свой магазин платформы заполнен → он. Пока свой адрес есть, чужой не
 *     подставляется НИКОГДА — ратифицированный критерий WEB-828 п.2;
 *   · своего нет, соседний заполнен → соседний. Пустой финал воронки хуже
 *     перехода в соседний магазин; так уже вела себя страница после оплаты, и
 *     именно её поведение владелец сделал общим;
 *   · не заполнено ничего → null, и вызывающий обязан сказать это человеку
 *     словами, а не подставлять выдуманный адрес (WEB-828 п.3).
 *
 * Десктоп и нераспознанный браузер ('other') — своего магазина нет вообще,
 * оба адреса одинаково чужие: порядок android → ios, ратифицированное правило
 * захвата трафика (WEB-271 §6, отклонение R5).
 *
 * ДВА КОНЦА (WEB-1844). Тот же ответ обязан давать бэк —
 * `pickStoreUrlForPlatform` в
 * `backend/src/projects/utils/project-store-urls.util.ts`. Письменный ответ у
 * правила ОДИН, таблицей `store-url-rule.cases.json` рядом с ней; обе стороны
 * гоняют её: `storeUrlRule.twoEnds.web1910.guard.test.ts` здесь и
 * `store-url-rule.web1910.spec.ts` на бэке.
 *
 * Ссылка с меткой перехода (opaque install-token, WEB-433) сильнее голой
 * ссылки на тот же магазин: сперва она, и только потом адрес из настроек. Это
 * не выбор МАГАЗИНА, а выбор формы одного и того же адреса, поэтому считается
 * до правила выше — на бэке этой пары нет, и в таблицу случаев она не входит.
 */
export function resolveStoreUrl(
  userAgent: string | undefined | null,
  targets: StoreTargets,
): string | null {
  const ios = blank(targets.installUrlIos) ?? blank(targets.iosStoreUrl);
  const android =
    blank(targets.installUrlAndroid) ?? blank(targets.androidStoreUrl);

  if (detectStorePlatform(userAgent) === 'ios') {
    return ios ?? android;
  }
  // 'android' и 'other' совпадают по построению: у первого свой магазин —
  // Google Play, у второго своего нет и действует захват трафика android→ios.
  return android ?? ios;
}

export interface OpenAppOrStoreOptions extends StoreTargets {
  /** Готовая ссылка-схема возврата в приложение (собирает бэкенд). */
  schemeUrl?: string | null;
  /** UA; по умолчанию — navigator.userAgent. */
  userAgent?: string;
  /** Переопределение таймера попытки (для тестов/подстройки). */
  timeoutMs?: number;
  /**
   * Зовётся, когда попытка открыть приложение завершилась — успехом (страница
   * ушла в фон) или уходом в магазин. Нужен, чтобы снять с экрана текст
   * «Открываем приложение…».
   */
  onSettled?: () => void;
}

export type OpenAppOutcome =
  /** Пробуем приложение; магазин — по таймеру, если оно не открылось. */
  | 'app-attempt'
  /** Ушли прямо в магазин (десктоп либо схемы нет). */
  | 'store'
  /** Вести некуда — вызывающий обязан сказать это человеку. */
  | 'none';

export function openAppOrStore(
  options: OpenAppOrStoreOptions,
): OpenAppOutcome {
  const userAgent =
    options.userAgent ??
    (typeof navigator !== 'undefined' ? navigator.userAgent : '');
  const storeUrl = resolveStoreUrl(userAgent, options);
  const schemeUrl = blank(options.schemeUrl);
  const platform = detectStorePlatform(userAgent);

  // Десктоп: приложения там нет по определению — схему не дёргаем вовсе
  // (браузер показал бы модалку «не удалось открыть»), сразу магазин.
  if (!schemeUrl || platform === 'other') {
    if (storeUrl) {
      deeplinkNavigate(storeUrl);
      return 'store';
    }
    return 'none';
  }

  const timeoutMs = options.timeoutMs ?? APP_ATTEMPT_TIMEOUT_MS;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const cleanup = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    document.removeEventListener('visibilitychange', onVisibility);
  };

  function onVisibility() {
    // Страница ушла в фон = приложение открылось. Магазин отменяем.
    if (document.hidden) {
      cleanup();
      options.onSettled?.();
    }
  }

  document.addEventListener('visibilitychange', onVisibility);

  timer = setTimeout(() => {
    timer = null;
    document.removeEventListener('visibilitychange', onVisibility);
    if (!document.hidden && storeUrl) {
      deeplinkNavigate(storeUrl);
    }
    options.onSettled?.();
  }, timeoutMs);

  // Попытка идёт ПОСЛЕ постановки таймера: иначе на быстром устройстве
  // переход в приложение успел бы случиться до того, как мы подписались на
  // visibilitychange, и мы бы всё равно уехали в магазин поверх приложения.
  deeplinkNavigate(schemeUrl);

  return 'app-attempt';
}
